import { isBefore, startOfMonth, subMonths } from "date-fns";
import { useEffect, useState } from "react";

import { loadLocale } from "@smart/itops-locale-dom";

import { DayPicker } from "./day-picker";
import { MonthPicker } from "./month-picker";
import { MAX_PAST_MONTHS } from "./types";

type DatePickerProps = {
  onDateChange: (date: Date) => void;
  selectedDate?: Date;
  disabledDays?: {
    before?: Date;
    after?: Date;
  };
  dataTestId?: string;
  unrestricted?: boolean;
};

export const DatePicker = ({
  onDateChange,
  selectedDate,
  disabledDays,
  dataTestId,
  unrestricted,
}: DatePickerProps) => {
  const today = new Date();

  const earliestAllowed = startOfMonth(subMonths(today, MAX_PAST_MONTHS));
  const isValidSelected =
    selectedDate && (unrestricted || !isBefore(selectedDate, earliestAllowed));

  const initialYear = isValidSelected
    ? selectedDate.getFullYear()
    : today.getFullYear();

  const initialMonth = isValidSelected
    ? selectedDate.getMonth()
    : today.getMonth();

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);

  useEffect(() => {
    if (isValidSelected) {
      setYear(selectedDate.getFullYear());
      setMonth(selectedDate.getMonth());
    }
  }, []);

  return (
    <div
      data-testid={`${dataTestId}-date-picker`}
      className="flex flex-col gap-medium p-[1.6rem] w-[31.4rem] shadow-dropdownMenu rounded z-modal relative bg-white"
    >
      <MonthPicker
        year={year}
        month={month}
        setYear={setYear}
        setMonth={setMonth}
        earliestAllowed={unrestricted ? undefined : earliestAllowed}
      />
      <DayPicker
        year={year}
        month={month}
        onDateChange={onDateChange}
        startsOnSunday={loadLocale().country === "US"}
        selectedDate={isValidSelected ? selectedDate : undefined}
        disabledDays={disabledDays}
      />
    </div>
  );
};
